/**
 * Central launch + company configuration.
 *
 * CURRENT STATE: public launch. KittyPot is live on Google Play and the
 * App Store.
 *
 * Store availability is derived from the URLs below, NOT from a separate flag.
 * A store with an empty URL is treated as "not yet available" everywhere on the
 * site: no dead link is rendered, and the copy says it is coming soon.
 *
 * WHEN APPLE APPROVES: paste the App Store URL into APP_STORE_URL. Every
 * "coming soon to iPhone" line disappears and the iPhone download button
 * appears automatically. No other change is needed.
 *
 * See docs/internal-pre-launch-checklist.md for the outstanding legal and
 * feature-verification items. That file is internal and is not rendered.
 */

export type LaunchState = 'pre-launch' | 'public-launch'

export const DEFAULT_LAUNCH_STATE: LaunchState = 'public-launch'

/** Live store URLs. An empty string means "not released on that platform yet". */
export const APP_STORE_URL =
  'https://apps.apple.com/gb/app/kittypot-split-expenses/id6759958370'
export const GOOGLE_PLAY_URL =
  'https://play.google.com/store/apps/details?id=com.kittypot.app'

/**
 * Official store badge artwork, downloaded from Apple's Marketing Resources and
 * Google Play's Brand Guidelines. Home-made lookalikes breach both sets of
 * brand guidelines, so these must be the official assets — leave empty until
 * they are in place.
 */
export const STORE_BADGE_ASSETS = {
  // Both badges come from one supplied image (Google Play above, App Store
  // below); each is cropped out of it via STORE_BADGE_CROPS.
  appStoreBadgeSrc:
    'https://cdn.magicpatterns.com/uploads/fdpvfzhfqE69Kzu7ZYH68C/apple-app-store-travel-awards-globestamp-7.png',
  googlePlayBadgeSrc:
    'https://cdn.magicpatterns.com/uploads/fdpvfzhfqE69Kzu7ZYH68C/apple-app-store-travel-awards-globestamp-7.png',
}

/**
 * Some official badge downloads ship on a large square canvas with transparent
 * padding around the artwork. Rendering those by height alone makes the badge
 * look tiny, so each asset can declare where the artwork actually sits inside
 * its canvas, as fractions of the image's own width/height. The badge is then
 * cropped to that region and scaled to the required height.
 *
 * Use null when the asset is already tightly cropped.
 */
export type BadgeCrop = {
  top: number
  left: number
  width: number
  height: number
  /** Source image width ÷ height. Defaults to 1 (square canvas). */
  aspect?: number
}

export const STORE_BADGE_CROPS: {
  appStore: BadgeCrop | null
  googlePlay: BadgeCrop | null
} = {
  // 3:2 canvas holding both badges, stacked.
  googlePlay: { top: 0.066, left: 0.054, width: 0.894, height: 0.393, aspect: 1.5 },
  appStore: { top: 0.541, left: 0.054, width: 0.894, height: 0.393, aspect: 1.5 },
}

export const SITE_URL = 'https://www.kittypot.com'

/** Browser companion — join a kitty from an invite without installing the app. */
export const WEB_JOIN_URL = 'https://join.kittypot.com'

export const SUPPORT_EMAIL = 'support@kittypot.com'

export const COMPANY = {
  legalName: 'KittyPot Ltd',
  brandName: 'KittyPot',
  registrationNumber: '13695043',
  /** Registered office. Confirmed against Companies House. */
  addressLines: [
    'KittyPot Ltd',
    'Flat 2 Minavon',
    'Tyn Y Parc Road',
    'Cardiff',
    'Wales',
    'CF14 6BE',
  ],
  /** One-line form, for discreet placements such as the footer. */
  addressOneLine:
    'Flat 2 Minavon, Tyn Y Parc Road, Cardiff, Wales, CF14 6BE',
  copyrightYear: 2026,
}

/**
 * Master switch for non-essential technologies (analytics / marketing).
 *
 * CURRENT STATE: false. The website loads NO analytics or marketing scripts at
 * all, sets only strictly necessary storage, and the consent banner is hidden.
 *
 * Setting this to true does two things and only two things:
 *  1. Shows the consent banner (components/CookieBanner.tsx).
 *  2. Switches the Cookie Policy's analytics section to the "with consent"
 *     wording.
 *
 * It does NOT itself load any script, because there are no scripts to load yet.
 * When you add analytics, add the loader inside loadConsentedScripts() in
 * utils/consent.ts — that function is the single gate and is only ever called
 * after consent is granted.
 */
export const USES_NON_ESSENTIAL_COOKIES = false
